// Brickminded — Fit-to-width headings.
//
// Two modes, both driven by --fit-scale: the element's CSS sizes the font as
// calc(<max> * var(--fit-scale, 1)) and this script sets the measured factor.
//
// [data-bm-fit] — ONE LINE. The whole text is scaled down just enough to fit
// the width the element is given, instead of wrapping.
//
//   <h2 class="…" data-bm-fit>Stöber nach Kategorie.</h2>          // default floor
//   <h1 class="…" data-bm-fit="0.42">Steine sind erst der …</h1>   // custom floor
//
// [data-bm-fit-word] — NO BROKEN WORDS. The text wraps over as many lines as
// it likes at full size; the font is scaled only when a single word is wider
// than the line and would otherwise be broken mid-word. So a multi-word title
// keeps its size and simply wraps onto two lines, while a long compound noun
// ("Dinosauriermuseum") shrinks by the few percent that make it fit.
//
//   <h1 class="…" data-bm-fit-word="0.6">{{ product.title }}</h1>
//
// In both modes the attribute value is the minimum scale (floor). Below it
// [data-bm-fit] lets the text wrap (white-space: normal) rather than shrink
// into illegibility, and [data-bm-fit-word] returns to full size and leaves
// the break to the CSS — a shrink that still breaks the word buys nothing.
//
// Grouping — [data-bm-fit-group="name"] (one-line mode only): all elements
// sharing a group name are scaled to the SAME factor (the smallest one any
// member needs), so headings of the same kind end up the same size and the
// tightest one still fits. Members should share the same max font-size in CSS
// for the sizes to actually match.
//
// Give each element the width it should fit against (block box → its container;
// next to a button use flex:1 + min-width:0 on the wrapper).
//
// Markup swapped in by script (the cart's Section-Rendering-API refresh) brings
// fit nodes nothing observes yet — call window.BMFitText.refresh(scope) after it.

(function () {
  if (window.__bmFitText) return;
  window.__bmFitText = true;

  var DEFAULT_FLOOR = 0.5;
  var LINE_ATTR = 'data-bm-fit';
  var WORD_ATTR = 'data-bm-fit-word';
  var SELECTOR = '[' + LINE_ATTR + '], [' + WORD_ATTR + ']';

  function floorOf(el, attr) {
    var f = parseFloat(el.getAttribute(attr));
    return f > 0 && f <= 1 ? f : DEFAULT_FLOOR;
  }

  // Width available inside the element's own box. clientWidth rounds to whole
  // pixels, which is enough to leave a word a pixel too wide, so measure off
  // the rect instead.
  function contentWidth(el) {
    var cs = window.getComputedStyle(el);
    var w =
      el.getBoundingClientRect().width -
      parseFloat(cs.paddingLeft) -
      parseFloat(cs.paddingRight) -
      parseFloat(cs.borderLeftWidth) -
      parseFloat(cs.borderRightWidth);
    return w > 0 ? w : 0;
  }

  // Safari only learned the unprefixed property in 17.
  function setHyphens(el, value) {
    el.style.setProperty('-webkit-hyphens', value);
    el.style.setProperty('hyphens', value);
  }

  // Measure the element's "needed" scale at full size (scale 1). scrollWidth
  // only reports the real text width when it overflows the box; when the text
  // fits, scrollWidth == clientWidth. So we must measure at scale 1 — otherwise
  // the text could only ever shrink and never grow back.
  function measure(el) {
    el.style.whiteSpace = 'nowrap';
    el.style.setProperty('--fit-scale', '1');
    var avail = el.clientWidth;
    var need = el.scrollWidth;
    return { need: need, needed: need && need > avail ? avail / need : 1 };
  }

  function apply(el, scale, needed) {
    // One line as long as the (group) scale still fits this element; else wrap.
    el.style.whiteSpace = scale <= needed + 0.002 ? 'nowrap' : 'normal';
    el.style.setProperty('--fit-scale', scale.toFixed(3));
  }

  // Fit one bucket of elements to a shared scale (a single-element bucket is
  // just an individual heading).
  function fitList(members) {
    var list = [];
    var min = 1;
    var floor = DEFAULT_FLOOR;
    for (var i = 0; i < members.length; i++) {
      var el = members[i];
      if (!el.isConnected || !el.clientWidth) continue;
      floor = Math.max(floor, floorOf(el, LINE_ATTR));
      var m = measure(el);
      if (!m.need) {
        el.style.whiteSpace = '';
        continue;
      }
      list.push({ el: el, needed: m.needed });
      if (m.needed < min) min = m.needed;
    }
    if (!list.length) return;
    var scale = Math.max(floor, min);
    for (var j = 0; j < list.length; j++) apply(list[j].el, scale, list[j].needed);
  }

  // Word mode — measures the longest WORD, not the line. With breaking and
  // hyphenation switched off, an element's min-content width is exactly the
  // width of its widest unbreakable run; only if that overflows does the font
  // scale down. Needs a block-level element, since min-content is read by
  // briefly setting width: min-content on it.
  //
  // Its container must not be floored by its own content (a `1fr` grid track
  // is `minmax(auto, 1fr)`) — relaxing the breaks would widen the track, and
  // the scale and the track would then chase each other.
  function fitWord(el) {
    if (!el.isConnected) return;

    el.style.setProperty('--fit-scale', '1');
    var avail = contentWidth(el); // read before the breaks below are relaxed
    if (!avail) return;

    var prevWidth = el.style.width;
    el.style.wordBreak = 'normal';
    el.style.overflowWrap = 'normal';
    setHyphens(el, 'manual');
    el.style.width = 'min-content';
    var longest = contentWidth(el);
    el.style.width = prevWidth;

    var needed = longest > avail + 0.5 ? avail / longest : 1;
    if (needed < floorOf(el, WORD_ATTR)) {
      // Even at the floor the word would not fit, so shrinking gains nothing —
      // stay at full size and hand the break back to the CSS.
      el.style.wordBreak = '';
      el.style.overflowWrap = '';
      setHyphens(el, '');
      return;
    }
    el.style.setProperty('--fit-scale', needed.toFixed(3));
  }

  function fitAll() {
    var els = document.querySelectorAll(SELECTOR);
    var groups = {};
    var names = [];
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.hasAttribute(WORD_ATTR)) {
        fitWord(el);
        continue;
      }
      var g = el.getAttribute('data-bm-fit-group');
      if (g) {
        if (!groups[g]) {
          groups[g] = [];
          names.push(g);
        }
        groups[g].push(el);
      } else {
        fitList([el]);
      }
    }
    for (var n = 0; n < names.length; n++) fitList(groups[names[n]]);
  }

  // Batch resize reactions into one rAF pass. Doing the size changes here
  // (rather than synchronously inside the ResizeObserver callback) also avoids
  // the "ResizeObserver loop" churn from our own font-height changes.
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    var run = function () {
      scheduled = false;
      fitAll();
    };
    if (window.requestAnimationFrame) requestAnimationFrame(run);
    else setTimeout(run, 0);
  }

  var ro = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;

  function scan(scope) {
    var els = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.__bmFitInit) continue;
      el.__bmFitInit = true;
      if (ro) ro.observe(el);
    }
  }

  function boot() {
    scan(document);
    if (!ro) window.addEventListener('resize', schedule);
    fitAll();
    // Re-measure once web fonts have loaded (their metrics differ from the
    // fallback used at first paint).
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: a re-rendered section brings new fit nodes, and a
  // group may span sections — so observe the new nodes and re-fit everything.
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
    schedule();
  });

  window.BMFitText = {
    refresh: function (scope) {
      scan(scope);
      schedule();
    }
  };
})();
